package gr.tech.pdfgenerator.infrastructure.calculations

import gr.tech.pdfgenerator.models.DerivationsDTO
import gr.tech.pdfgenerator.models.ProposalCalculationsDTO
import gr.tech.pdfgenerator.models.ProposalInputsDTO
import gr.tech.pdfgenerator.util.calculateCostOfWaitingList
import gr.tech.pdfgenerator.util.calculateIRR
import gr.tech.pdfgenerator.util.createIRRInput
import gr.tech.pdfgenerator.util.cummulativeCashFlowCalculations
import gr.tech.pdfgenerator.util.format
import gr.tech.pdfgenerator.util.netCashFlowCalculations
import gr.tech.pdfgenerator.util.savingsListCalculations
import kotlin.math.absoluteValue
import kotlin.math.roundToLong

/*
    Project Proposal Calculations
*/
fun recalculateDerivations(
    inputState: ProposalInputsDTO,
): DerivationsDTO {
    val electricityRate = inputState.selectedStateElectricityRate
    val annualKWHSaved = inputState.baselineKWH - inputState.proposedKWH

    val withCentsLight = (annualKWHSaved * electricityRate)
    val energySavingsLighting = (withCentsLight * 100).toLong()

    val withCentsHVAC = ((energySavingsLighting / 100.00) * inputState.hvacInteractionSavings * inputState.hvacUpTime)
    val energySavingsHVAC = (withCentsHVAC.format(2).toDouble() * 100).toLong()

    val inCentsMaint = (inputState.hpsMaintenance * inputState.baselineFixCount)
    val maintenanceSavings = inCentsMaint

    return DerivationsDTO(
        electricityRate = electricityRate,
        annualKWHSaved = annualKWHSaved,
        energySavingsLighting = energySavingsLighting,
        energySavingsHVAC = energySavingsHVAC,
        maintenanceSavings = maintenanceSavings
    )
}


fun recalculateProposalCalculations(
    inputState: ProposalInputsDTO,
    derivationsState: DerivationsDTO
): ProposalCalculationsDTO {
    /*
        Constants Used in calculating
        Refer to sheet
     */
    val salesTaxDecimal: Double = 0.075  //Standard Value from Sheet
    val usYearlyTotalkWh: Long = 4100000000000
    val usYearlyTotalco2: Long = 1650000000
    //Source: https://www.eia.gov/tools/faqs/faq.php?id=74&t=11
    val vehicleCo2Year: Double = 4.6
    //Source: https://www.epa.gov/greenvehicles/greenhouse-gas-emissions-typical-passenger-vehicle#:~:text=typical%20passenger%20vehicle%3F-,A%20typical%20passenger%20vehicle%20emits%20about%204.6%20metric%20tons%20of,8%2C887%20grams%20of%20CO2.
    val treeCo2Year: Double = 0.02177
    //Source: https://www.usda.gov/media/blog/2015/03/17/power-one-tree-very-air-we-breathe#:~:text=According%20to%20the%20Arbor%20Day,the%20very%20air%20we%20breathe.

    //Linked Values
    val replaceFixBulbs = (derivationsState.maintenanceSavings * 10)
    val projectCost = inputState.projectCost
    val salesTaxCalc = (inputState.projectCost * salesTaxDecimal).toLong()
    val totalProjectCost = inputState.projectCost + salesTaxCalc
    val rebates = inputState.rebate
    val netProjectCost = totalProjectCost - rebates
    val simplePayback =
        if ((derivationsState.energySavingsLighting + derivationsState.energySavingsHVAC + derivationsState.maintenanceSavings) <= 0) {
            0.0
        } else {
            netProjectCost.absoluteValue.toDouble() / (derivationsState.energySavingsLighting + derivationsState.energySavingsHVAC + derivationsState.maintenanceSavings).toDouble()
        }

    val energySavingsLighting = derivationsState.energySavingsLighting
    val energySavingsHVAC = derivationsState.energySavingsHVAC
    val maintenanceSavings = derivationsState.maintenanceSavings
    val totalAnnualSavings = derivationsState.energySavingsLighting + derivationsState.energySavingsHVAC + derivationsState.maintenanceSavings

    //Cash Flows & Financing (Co Dependent on Some values)
    val cashFlowProjectCost = -(netProjectCost)
    val energySavingList = savingsListCalculations(derivationsState.energySavingsLighting, 1.03)
    val hvacSavingsList = savingsListCalculations(derivationsState.energySavingsHVAC, 1.03)
    val netCashFlowList = netCashFlowCalculations(
        cashFlowProjectCost,
        energySavingList,
        hvacSavingsList,
        derivationsState.maintenanceSavings
    )
    val cumulativeCashFlowList = cummulativeCashFlowCalculations(netCashFlowList)

    val totalROI = (cumulativeCashFlowList.last() / netProjectCost.absoluteValue.toDouble()) //From Linked Values, but has dependency.

    val financedAmount = (rebates * 0.8).toLong()
    val upFrontNoFinance = totalProjectCost
    val upFrontFinanced = upFrontNoFinance - financedAmount

    val cashflowFirstColumnIRRSum = (derivationsState.energySavingsLighting + derivationsState.energySavingsHVAC + derivationsState.maintenanceSavings)
    val irrInput1 = createIRRInput(cashFlowProjectCost, cashflowFirstColumnIRRSum, netCashFlowList)
    val irrInput2 = createIRRInput(-(totalProjectCost), cashflowFirstColumnIRRSum, netCashFlowList.take(5))
    val irrInput3 =
        createIRRInput(-(totalProjectCost + (-financedAmount)), cashflowFirstColumnIRRSum, netCashFlowList.take(5))

    val returnNoFinance = calculateIRR(irrInput2)
    val returnFinanced = calculateIRR(irrInput3)

    //Cost Waiting
    val costInYearsList =
        calculateCostOfWaitingList(energySavingList, hvacSavingsList, derivationsState.maintenanceSavings)

    val annualizedROI = calculateIRR(irrInput1) //From Linked Values, but has dependency.

    //savings analysis
    val baseECostWithCents = (inputState.baselineKWH * derivationsState.electricityRate)
    val baselineEnergyCosts = (baseECostWithCents * 100).toLong()       //* 100 to turn dollar.cents into cents(Long).
    val ledECostWithCents = (inputState.proposedKWH * derivationsState.electricityRate)
    val ledEnergyCosts = (ledECostWithCents * 100).toLong()             //* 100 to turn dollar.cents into cents(Long).
    val baseHVACWithCents = (inputState.baselineKWH * 0.03)
    val baselineHVAC = (baseHVACWithCents * 100).toLong()
    val baselineMaintenance = derivationsState.maintenanceSavings
    val ledHVAC = baselineHVAC - derivationsState.energySavingsHVAC
    val baselineTotal = baselineEnergyCosts + baselineMaintenance + baselineHVAC
    val ledMaintenance = 0L
    val ledTotal = ledEnergyCosts + ledMaintenance + ledHVAC
    val annualSavings = baselineTotal - ledTotal

    var baselineCostSavings = 0.0
    var ledCostSavings = 0.0
    if (baselineTotal > 0) {
        baselineCostSavings = (annualSavings.toDouble() / baselineTotal.toDouble())
        ledCostSavings = 1 - baselineCostSavings
    }

    //environmental impacts
    val totalEnergySavings = (derivationsState.annualKWHSaved * (1 + (inputState.hvacInteractionSavings * inputState.hvacUpTime)))
    val tonsCo2kWh = usYearlyTotalco2 / usYearlyTotalkWh.toDouble()
    val co2Reduced = (totalEnergySavings * tonsCo2kWh)
    val baselineCo2Amount = (inputState.baselineKWH * tonsCo2kWh)
    val co2ReducedPercentage = (co2Reduced / baselineCo2Amount)
    val carsRemoved = (co2Reduced / vehicleCo2Year).roundToLong()
    val treesPlanted = (co2Reduced / treeCo2Year).roundToLong()


    return ProposalCalculationsDTO(
        //Linked Values
        totalROI = totalROI,
        annualizedROI = annualizedROI,
        simplePayback = simplePayback,

        replaceFixBulbs = replaceFixBulbs,
        projectCost = projectCost,
        salesTax = salesTaxCalc,
        totalProjectCost = totalProjectCost,
        rebates = rebates,
        netProjectCost = netProjectCost,

        energySavingsLighting = energySavingsLighting,
        energySavingsHVAC = energySavingsHVAC,
        maintenanceSavings = maintenanceSavings,
        totalAnnualSavings = totalAnnualSavings,
        totalSavingsTenYears = cumulativeCashFlowList.last(),

        //Cash Flow
        cashflowProjectCost = -(netProjectCost),
        energySavingList = energySavingList,
        hvacSavingsList = hvacSavingsList,
        netCashFlowList = netCashFlowList,
        cummCashFlowList = cumulativeCashFlowList,
        irrInput = irrInput1,
        irrInput2 = irrInput2,
        irrInput3 = irrInput3,

        //Finance
        financedAmount = financedAmount,
        upfrontNoFinance = upFrontNoFinance,
        upfrontFinanced = upFrontFinanced,
        returnNoFinance = returnNoFinance,
        returnFinanced = returnFinanced,

        //cost waiting
        costInYearsList = costInYearsList,

        //savings analysis
        annualSavings = annualSavings,

        baselineCostSavings = baselineCostSavings,
        baselineEnergyCosts = baselineEnergyCosts,
        baselineMaintenance = baselineMaintenance,
        baselineHVAC = baselineHVAC,
        baselineTotal = baselineTotal,

        ledCostSavings = ledCostSavings,
        ledEnergyCosts = ledEnergyCosts,
        ledMaintenance = ledMaintenance,
        ledHVAC = ledHVAC,
        ledTotal = ledTotal,

        //environmental impacts
        totalEnergySavings = totalEnergySavings,
        baselineCo2Amount = baselineCo2Amount,
        co2Reduced = co2Reduced,
        co2ReducedPercentage = co2ReducedPercentage,
        carsRemoved = carsRemoved,
        treesPlanted = treesPlanted,
        tonsCo2kWh = tonsCo2kWh,
    )
}

